'use client';

import { useState } from 'react';
import { useSWRConfig } from 'swr';
import { FunnelIcon, TrashIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { notifyError } from '@/lib/notify-error';
import { track } from '@/lib/telemetry';
import { categoryBudgetApi } from '@/lib/api';
import { useCategoryRules } from '@/hooks/queries/useCategoryRules';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';

/**
 * A category's merchant rules — list + delete. Pluggy refs roll up their
 * subtree server-side, so a root shows its leaves' rules too. Self-contained
 * data-wise (fetch + delete + cache invalidation), so hosts only pass the ref;
 * today it renders inside the Category Hub's Regras view.
 */
export function CategoryRulesList({
  categoryRef,
  categoryName,
}: {
  categoryRef: string | null;
  /** Suppresses the redundant "→ category" line when a rule targets the ref itself. */
  categoryName?: string;
}) {
  const authedFetch = useAuthedFetcher();
  const { data: rules, isLoading } = useCategoryRules(categoryRef);
  const { mutate: mutateGlobal } = useSWRConfig();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (ruleId: string) => {
    setDeletingId(ruleId);
    try {
      await authedFetch((token) => categoryBudgetApi.deleteRule(token, ruleId));
      track('action.category_rule.delete');
      toast.success('Regra removida — os lançamentos já categorizados não mudam.');
      // This category's list AND the page-level "all rules" set (which gates the kebab item).
      void mutateGlobal((key) => Array.isArray(key) && key[0] === 'category-rules');
    } catch {
      notifyError('category_rules', null, 'Não foi possível remover a regra.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="space-y-2 px-4 py-4">
        {isLoading && !rules ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-muted" />
            ))}
          </div>
        ) : !rules || rules.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <FunnelIcon className="size-6 text-text-tertiary" weight="duotone" />
            <p className="text-sm text-text-secondary">Nenhuma regra para esta categoria.</p>
            <p className="max-w-64 text-xs text-text-tertiary">
              Crie uma ao confirmar lançamentos na fila “A revisar” ou ao categorizar em lote.
            </p>
          </div>
        ) : (
          rules.map((rule) => (
            <div
              key={rule.id}
              className="flex items-center gap-3 rounded-xl border border-border-subtle px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-text-primary">
                    {/* The curated brand reads LIVE off the merchant; displayLabel is the
                        snapshot taken when the rule was minted. */}
                    {rule.merchant?.brandName ?? rule.displayLabel}
                  </span>
                  {rule.target === 'merchant' ? (
                    <span className="shrink-0 rounded-full border border-border bg-surface px-1.5 py-0.5 text-3xs font-medium text-text-secondary">
                      estabelecimento
                    </span>
                  ) : (
                    <span className="shrink-0 rounded-full bg-surface-sunken px-1.5 py-0.5 text-3xs font-medium text-text-tertiary">
                      descrição
                    </span>
                  )}
                </p>
                {/* A merchant rule has no matchValue — the anchor is the identity, not a
                    string — so the mono line would render empty. Say what it follows. */}
                {rule.target === 'merchant' ? (
                  <p className="truncate text-2xs text-text-tertiary">
                    {rule.merchant?.descriptorCount
                      ? `${rule.merchant.descriptorCount} ${
                          rule.merchant.descriptorCount === 1 ? 'descritor' : 'descritores'
                        } · `
                      : ''}
                    Segue merges e renomeações do estabelecimento
                  </p>
                ) : (
                  <p className="truncate font-mono text-2xs text-text-tertiary">
                    {rule.matchValue}
                  </p>
                )}
                {rule.categoryName !== categoryName && (
                  <p className="truncate text-2xs text-text-secondary">
                    → {rule.categoryName}
                  </p>
                )}
                <p className="text-2xs text-text-tertiary">
                  {rule.appliedCount === 1 ? 'aplicada 1 vez' : `aplicada ${rule.appliedCount} vezes`}
                  {rule.lastAppliedAt && (
                    <span>
                      {' · última em '}
                      {new Date(rule.lastAppliedAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'short',
                        timeZone: 'UTC',
                      })}
                    </span>
                  )}
                </p>
              </div>
              <button
                type="button"
                aria-label={`Remover regra de ${rule.merchant?.brandName ?? rule.displayLabel}`}
                disabled={deletingId === rule.id}
                onClick={() => handleDelete(rule.id)}
                className="grid size-8 shrink-0 place-items-center rounded-lg text-text-tertiary transition-colors hover:bg-error-surface hover:text-error-text disabled:opacity-50"
              >
                <TrashIcon className="size-4" />
              </button>
            </div>
          ))
        )}
      </div>

      <div className="border-t border-border-subtle px-4 py-2.5 text-2xs text-text-tertiary">
        Remover uma regra nunca descategoriza o passado — só os próximos lançamentos deixam
        de ser categorizados automaticamente.
      </div>
    </div>
  );
}
